import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { PackagingDto } from 'src/app/shared/dto/packaging.dto';
import { PackagingResponseDto } from 'src/app/shared/dto/pagination/packagings-response.dto';
import { RequestPackagingBatchDto } from 'src/app/shared/dto/request-packaging-batch.dto';
import { BaseService } from 'src/app/shared/services/base.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { Client, formatClientRoom, RoomNames } from 'src/app/shared/sockets/enums/rooms';

@Injectable({
    providedIn: 'root',
})
export class PackagingsService extends BaseService {
    private packagingsSubject = new BehaviorSubject<PackagingDto[]>([]);
    public packagings$ = this.packagingsSubject.asObservable();

    private readonly socketService = inject(SocketService);

    constructor(httpClient: HttpClient) {
        super(httpClient, 'packagings');
    }

    async setupWebSocket(): Promise<void> {
        try {
            this.packagingsSubject.next((await this.findAll()).packagings);
            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PACKAGINGS_ROOM));
            this.socketService.getNotifications(Messages.PACKAGINGS_CHANGED).subscribe(async () => {
                const response = await this.findAll();
                this.packagingsSubject.next(response.packagings);
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de formatos. Error: ${e.message}`);
        }
    }

    disconnectWebSocket(): void {
        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PACKAGINGS_ROOM));
    }

    async findAll(): Promise<PackagingResponseDto> {
        try {
            const request = this.httpClient.get<PackagingResponseDto>(`${this.url}/findAll`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha encontrado ningun formato. Error: ${e.message}`);
        }
    }

    async create(packaging: PackagingDto): Promise<PackagingDto> {
        try {
            const request = this.httpClient.post<PackagingDto>(`${this.url}`, packaging);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido crear el formato. Error: ${e.message}`);
        }
    }

    async getPackagingsByArticleId(articleId: number): Promise<PackagingDto[]> {
        try {
            if (!articleId || articleId <= 0) {
                throw new Error('El id del articulo ha de ser mayor de 0');
            }

            const request = this.httpClient.get<PackagingDto[]>(`${this.url}/findByArticleId/${articleId}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se han podido obtener los paquetes para el artículo ${articleId}. Error: ${e.message}`);
        }
    }

    async getPackagingsByArticleBatchId(articleBatchId: number): Promise<PackagingDto[]> {
        try {
            if (!articleBatchId || articleBatchId <= 0) {
                throw new Error('El id del lote ha de ser mayor de 0');
            }

            const request = this.httpClient.get<PackagingDto[]>(`${this.url}/findByArticleBatchId/${articleBatchId}`);

            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se han podido obtener los paquetes para el lote ${articleBatchId}. Error: ${error.message}`);
        }
    }

    async getPackagingsByFinalProductId(finalProductId: number): Promise<PackagingDto[]> {
        try {
            if (!finalProductId || finalProductId <= 0) {
                throw new Error('El id del producto final ha de ser mayor de 0');
            }

            const request = this.httpClient.get<PackagingDto[]>(`${this.url}/findByFinalProductId/${finalProductId}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se han podido obtener los paquetes para el producto final ${finalProductId}. Error: ${e.message}`);
        }
    }

    async getPackagingsByProcessId(processId: number): Promise<PackagingDto[]> {
        try {
            if (!processId || processId <= 0) {
                throw new Error('El id del proceso ha de ser mayor de 0');
            }

            const request = this.httpClient.get<PackagingDto[]>(`${this.url}/findByProcessId/${processId}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se han podido obtener las etiquetas para el proceso ${processId}. Error: ${e.message}`);
        }
    }

    async setPackagingAsConsumed(packagingElementId: number): Promise<boolean> {
        try {
            if (!packagingElementId || packagingElementId <= 0) {
                throw new Error('El id del formato ha de ser mayor de 0');
            }

            //TODO: update sending object, not just id
            // const request = this.httpClient.put<boolean>(`${this.url}/setPackagingAsConsumed/${packagingElementId}`, {});
            this.httpClient.put<boolean>(`${this.url}/setPackagingAsConsumed/${packagingElementId}`, {});

            // return await lastValueFrom(request);
            return true;
        } catch (e: any) {
            throw new Error(`No se ha podido consumir el formato con id ${packagingElementId}. Error: ${e.message}`);
        }
    }

    async updatePackagingsQuantity(packagings: PackagingDto[]): Promise<PackagingDto[]> {
        try {
            const request = this.httpClient.post<PackagingDto[]>(`${this.url}/updatePackagingsQuantity`, { packagings: packagings });

            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se ha podido actualizar la cantidad del formato. Error: ${error.message}`);
        }
    }

    async incrementPackagingQuantity(packagingId: number, quantity: number): Promise<PackagingDto> {
        try {
            const request = this.httpClient.patch<PackagingDto>(`${this.url}/incrementPackagingQuantity/${packagingId}`, {quantity});

            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se ha podido actualizar la cantidad del formato. Error: ${error.message}`);
        }
    }

    async sendToPrinter(packagingIds: number[], processId: number): Promise<void> {
        try {
            const request = this.httpClient.post<void>(`${this.url}/sendToPrinter`, { packagingIds: packagingIds, processId: processId });

            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se ha podido enviar las etiquetas a imprimir. Error: ${error.message}`);
        }
    }

    async requestNewPackagingBatchFromVelneo(processId: number, finalProductId: number, destinationType: string): Promise<PackagingDto> {
        try {
            const request = this.httpClient.post<PackagingDto>(`${this.url}/requestNewPackagingBatchFromVelneo`, {
                processId,
                finalProductId,
                destinationType
            });

            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se ha podido generar la etiqueta desde Velneo. Error: ${error.message}`);
        }
    }

    async assignPackagingsToArticle(packagingIds: number[], articleId: number): Promise<void> {
        try {
            const request = this.httpClient.post<void>(`${this.url}/assignPackagingsToArticle`, { packagingIds, articleId });

            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se han podido asignar los formatos al artículo. Error: ${error.message}`);
        }
    }

    async assignPackagingsToArticleBatch(packagingIds: number[], articleBatchId: number): Promise<void> {
        try {
            const request = this.httpClient.post<void>(`${this.url}/assignPackagingsToArticleBatch`, { packagingIds, articleBatchId });

            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se han podido asignar los formatos al lote. Error: ${error.message}`);
        }
    }

    async assignPackagingsToFinalProduct(packagingIds: number[], finalProductId: number): Promise<void> {
        try {
            const request = this.httpClient.post<void>(`${this.url}/assignPackagingsToFinalProduct`, { packagingIds, finalProductId });

            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se han podido asignar los formatos al producto final. Error: ${error.message}`);
        }
    }

    async handleRawMaterialsInPackagingsOnArticleOrOrderEvent(
        orderProcessId: number,
        userId: number,
        rawMaterialToUseId: number,
        newArticleId: number,
        newBatchNumber: string,
        oldArticleId?: number,
        oldBatchNumber?: string,
        lastPackage?: number
    ): Promise<void> {
        try {
            const body = {
                orderProcessId,
                userId,
                rawMaterialToUseId,
                newArticleId,
                newBatchNumber,
                oldArticleId,
                oldBatchNumber,
                lastPackage
            };

            const request = this.httpClient.post<void>(`${this.url}/handleRawMaterialsInPackagingsOnArticleOrOrderEvent`, body);

            return await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se han podido gestionar las materias primas en formatos. Error: ${error.message}`);
        }
    }

    async getPalletsByProcessId(processId: number): Promise<PackagingDto[]> {
        try {
            if (!processId || processId <= 0) {
                throw new Error('El id del proceso ha de ser mayor de 0');
            }

            const request = this.httpClient.get<PackagingDto[]>(`${this.url}/findPalletsByProcessId/${processId}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se han podido obtener los palets para el proceso ${processId}. Error: ${e.message}`);
        }
    }

    async consultPallets(externalId: number): Promise<PackagingDto[]> {
        try {
            if (!externalId || externalId <= 0) {
                throw new Error('El externalId de la orden ha de ser mayor de 0');
            }

            const request = this.httpClient.post<PackagingDto[]>(`${this.url}/consult-pallets`, { externalId });

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se han podido consultar los palets desde el conector. Error: ${e.message}`);
        }
    }

      /**
     * Llama al endpoint para rellenar cantidades de formatos hasta un packaging específico para un producto final.
     * @param finalProductId ID del producto final
     * @param lastPackagingId ID del último packaging hasta donde rellenar
     */
    async fillQuantitiesUpTo(finalProductId: number, lastPackagingId: number): Promise<void> {
        try {
            const request = this.httpClient.post<void>(`${this.url}/fillQuantitiesUpTo`, {
                finalProductId,
                lastPackagingId
            });
            await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se ha podido rellenar las cantidades de los formatos. Error: ${error.message}`);
        }
    }

    async printPalletLabels(processId: number, packagingIds: number[]): Promise<void> {
        try {
            const request = this.httpClient.post<void>(`${this.url}/printPalletLabels`, { processId, packagingIds });
            await lastValueFrom(request);
        } catch (error: any) {
            throw new Error(`No se ha podido imprimir la etiqueta del palet. Error: ${error.message}`);
        }
    }

}
